import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SubdomainSection from '../components/SubdomainSection';
import PresetForm from '../components/PresetForm';
import PresetCard from '../components/PresetCard';
import { Wordmark, PlusIcon, CardIcon } from '../components/Icons';

function Dashboard({ user, onLogout, onUpdateUser }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPreset, setEditingPreset] = useState(null);

  useEffect(() => {
    fetchPresets();
  }, []);

  const fetchPresets = async () => {
    try {
      const response = await axios.get('/api/presets');
      setPresets(response.data);
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePreset = async (presetData) => {
    try {
      const response = await axios.post('/api/presets', presetData);

      if (response.data.is_active) {
        setPresets([response.data, ...presets.map(p => ({ ...p, is_active: false }))]);
      } else {
        setPresets([response.data, ...presets]);
      }

      setShowForm(false);
    } catch (error) {
      console.error('Failed to create preset:', error);
      throw error;
    }
  };

  const handleUpdatePreset = async (id, presetData) => {
    try {
      const response = await axios.put(`/api/presets/${id}`, presetData);

      if (response.data.is_active) {
        setPresets(presets.map(p =>
          p.id === id ? response.data : { ...p, is_active: false }
        ));
      } else {
        setPresets(presets.map(p => p.id === id ? response.data : p));
      }

      setShowForm(false);
      setEditingPreset(null);
    } catch (error) {
      console.error('Failed to update preset:', error);
      throw error;
    }
  };

  const handleDeletePreset = async (id) => {
    if (!window.confirm('Delete this card? This cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api/presets/${id}`);
      setPresets(presets.filter(p => p.id !== id));
    } catch (error) {
      console.error('Failed to delete preset:', error);
    }
  };

  const handleActivatePreset = async (id) => {
    // Optimistic update so switching feels instant
    const previous = presets;
    setPresets(presets.map(p => ({ ...p, is_active: p.id === id })));

    try {
      await axios.post(`/api/presets/${id}/activate`);
    } catch (error) {
      console.error('Failed to activate preset:', error);
      setPresets(previous);
    }
  };

  const handleEditPreset = (preset) => {
    setEditingPreset(preset);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingPreset(null);
  };

  return (
    <div className="dashboard">
      <header className="topbar">
        <Wordmark />
        <div className="topbar-right">
          <span className="user-email">{user?.email}</span>
          <button className="btn btn-ghost btn-sm" onClick={onLogout}>
            Sign out
          </button>
        </div>
      </header>

      <main className="dashboard-content">
        <SubdomainSection user={user} onUpdateUser={onUpdateUser} />

        <section className="section">
          <div className="section-header">
            <h2 className="section-title">Contact cards</h2>
            {!showForm && (
              <button className="btn btn-outline btn-sm" onClick={() => setShowForm(true)}>
                <PlusIcon />
                New card
              </button>
            )}
          </div>
          <p className="section-description">
            The card marked as live is what people get when they open your link.
          </p>

          {showForm && (
            <PresetForm
              preset={editingPreset}
              onSave={editingPreset ?
                (data) => handleUpdatePreset(editingPreset.id, data) :
                handleCreatePreset
              }
              onCancel={handleCancelForm}
            />
          )}

          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2.5rem' }}>
              <div className="loading-spinner"></div>
            </div>
          ) : presets.length === 0 ? (
            !showForm && (
              <div className="empty-state">
                <CardIcon />
                <div className="empty-state-text">No contact cards yet</div>
                <div className="empty-state-subtext">
                  Create a card to start sharing your details
                </div>
              </div>
            )
          ) : (
            <div className="preset-list">
              {presets.map(preset => (
                <PresetCard
                  key={preset.id}
                  preset={preset}
                  onActivate={handleActivatePreset}
                  onEdit={handleEditPreset}
                  onDelete={handleDeletePreset}
                />
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default Dashboard;
